import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import { Form, FormInstance, Image } from "antd/es";
import { TooltipPlacement } from "antd/es/tooltip";
import { Component, KeyboardEvent, createRef } from "react";
import { OpenAIService } from "../../../services/openai-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import ChatboxEnterSvg from "../../../svgs/chatbox-enter.svg";
import ChatboxRobotAvatarSvg from "../../../svgs/chatbox-robot-avatar.svg";
import ChatboxUserAvatarSvg from "../../../svgs/chatbox-user-avatar.svg";
import CheckGreenSvg from "../../../svgs/check-green.svg";
import ClosePopupSvg from "../../../svgs/close-popup.svg";
import SpinSvg from "../../../svgs/spin.svg";
import UploadSvg from "../../../svgs/upload.svg";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../ui-kit/kuika-popover/kuika-popover";
import "./ai-image-generator.scss";

// The kit popover splits antd's single `placement` string into a side + an alignment.
// `placement` is this component's own public prop and its only caller
// (`components/common/file-picker.tsx`) is outside this migration's scope, so the antd
// vocabulary stays on the interface and is translated here instead of at the call site.
type KuikaPopoverPosition = "top" | "right" | "bottom" | "left";
type KuikaPopoverAlign = "start" | "center" | "end";

interface IKuikaPopoverPlacement {
  position: KuikaPopoverPosition;
  align: KuikaPopoverAlign;
}

const ANTD_PLACEMENT_TO_KIT: Record<TooltipPlacement, IKuikaPopoverPlacement> = {
  top: { position: "top", align: "center" },
  topLeft: { position: "top", align: "start" },
  topRight: { position: "top", align: "end" },
  bottom: { position: "bottom", align: "center" },
  bottomLeft: { position: "bottom", align: "start" },
  bottomRight: { position: "bottom", align: "end" },
  left: { position: "left", align: "center" },
  leftTop: { position: "left", align: "start" },
  leftBottom: { position: "left", align: "end" },
  right: { position: "right", align: "center" },
  rightTop: { position: "right", align: "start" },
  rightBottom: { position: "right", align: "end" }
};

// antd's Popover defaults to `top` (Tooltip's `placement = 'top'` default, which Popover
// inherits); the kit's own default is `bottom`, so an omitting caller must not silently flip.
const DEFAULT_ANTD_PLACEMENT: TooltipPlacement = "top";

enum EImageGeneratorMessageUserType {
  User = 0,
  AI = 1
}

enum EImageStatus {
  Idle,
  Uploading,
  Uploaded
}

interface IUploadQueueItem {
  messageIndex: number;
  imageIndex: number;
}

interface IGeneratedImage extends IImageLibraryDto {
  status: EImageStatus;
}

interface IFormSubmitProps {
  userMessage: string;
}

interface IImageGeneratorMessage {
  userType: EImageGeneratorMessageUserType;
  text?: string;
  generatedImages?: IGeneratedImage[];
}

export interface IAIImageGeneratorState {
  uploadQueue: IUploadQueueItem[];
  isAIGeneratingImages: boolean;
  messages: IImageGeneratorMessage[];
}
export interface IAIImageGeneratorProps {
  children?: React.ReactNode;
  placement?: TooltipPlacement;
  isActive: boolean;
  isUploaderBusy: boolean;
  uploadImage: (
    image: IImageLibraryDto[],
    cb?: {
      onPrev?: () => void;
      onSuccess?: () => void;
      onError?: () => void;
      onFinish?: () => void;
    }
  ) => void;
  onClose: () => void;
}

export class AIImageGenerator extends Component<IAIImageGeneratorProps, IAIImageGeneratorState> {
  formRef = createRef<FormInstance<any>>();

  messagesContainerRef = createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.state = {
      uploadQueue: [],
      isAIGeneratingImages: false,
      messages: []
    };
  }

  componentDidUpdate(): void {
    if (this.state.uploadQueue.length > 0 && !this.props.isUploaderBusy) {
      this.arrangeUploadQueue();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleCloseOnESC);
  }

  arrangeUploadQueue() {
    const queue = [...this.state.uploadQueue];
    this.setState({ uploadQueue: [] }, () => {
      this.handleUploadImage(queue);
    });
  }

  handleChangeImageStatus(messageIndex: number, imageIndex: number, status: EImageStatus) {
    const messages = [...this.state.messages];
    const msg = messages[messageIndex];
    if (!msg) return;
    if (!msg.generatedImages) return;

    const img = msg.generatedImages[imageIndex];
    img.status = status;
    this.setState({ messages });
  }

  handlePushToUploadQueue = (messageIndex: number, imageIndex: number) => {
    const uploadQueue = [...this.state.uploadQueue];

    if (uploadQueue.some((queueItem) => queueItem.imageIndex === imageIndex && queueItem.messageIndex === messageIndex))
      return;

    uploadQueue.push({ messageIndex, imageIndex });
    this.setState({ uploadQueue }, () => {
      this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Uploading);
    });
  };

  handleUploadImage = async (queue: IUploadQueueItem[]) => {
    const imagesToUpload: IImageLibraryDto[] = [];
    queue.forEach(({ imageIndex, messageIndex }) => {
      const messages = [...this.state.messages];
      const msg = messages[messageIndex];
      if (!msg) return;
      if (!msg.generatedImages) return;

      const img = msg.generatedImages[imageIndex];
      const image = { fileName: img.fileName, content: img.content } as IImageLibraryDto;
      imagesToUpload.push(image);
    });
    this.props.uploadImage(imagesToUpload, {
      // onPrev: () => {
      //   queue.forEach(({ messageIndex, imageIndex }) => {
      //     this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Uploading)
      //   })
      // },
      onSuccess: () => {
        queue.forEach(({ messageIndex, imageIndex }) => {
          this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Uploaded);
        });
      },
      onError: () => {
        queue.forEach(({ messageIndex, imageIndex }) => {
          this.handleChangeImageStatus(messageIndex, imageIndex, EImageStatus.Idle);
        });
      }
    });
  };

  handleSendImageGenerateRequest = (data?: IFormSubmitProps) => {
    let prompt = "";
    if (data) {
      prompt = data.userMessage;
      this.formRef.current?.resetFields();
      this.handlePushMessage({ userType: EImageGeneratorMessageUserType.User, text: prompt });
    } else {
      const userMessages = this.state.messages.filter((msg) => msg.userType === EImageGeneratorMessageUserType.User);
      if (userMessages.length === 0) return;

      prompt = userMessages[userMessages.length - 1].text as string;
    }

    this.setState({ isAIGeneratingImages: true });

    OpenAIService.GenerateImage({
      prompt,
      size: "1024x1024",
      numberOfImage: 1
    })
      .then(({ data: { images } }) => {
        const robotMessage: IImageGeneratorMessage = {
          userType: EImageGeneratorMessageUserType.AI,
          generatedImages: images.map(
            (content, i) =>
              ({
                fileName: `${Date.now()}_${i}.png`,
                content,
                status: EImageStatus.Idle
              }) as IGeneratedImage
          )
        };
        this.handlePushMessage(robotMessage);
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.setState({ isAIGeneratingImages: false });
      });
  };

  handleInputKeydown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    switch (e.code) {
      case "Enter":
        e.preventDefault();
        this.formRef.current?.submit();
        break;
      case "Escape":
        e.preventDefault();
        this.handleClosePopup();
        break;
    }
  };

  handlePopupVisibilityChange = (visible: boolean) => {
    if (!visible && !this.props.isActive) {
      document.removeEventListener("keydown", this.handleCloseOnESC);
      this.formRef.current?.resetFields();
      setTimeout(() => {
        this.setState({ messages: [], uploadQueue: [] });
      }, 300);

      return;
    }
    document.addEventListener("keydown", this.handleCloseOnESC);
  };

  handleClosePopup = () => {
    this.props.onClose();
    document.removeEventListener("keydown", this.handleCloseOnESC);
    this.formRef.current?.resetFields();
    setTimeout(() => {
      this.setState({ messages: [] });
    }, 300);
  };

  handleCloseOnESC = (e) => {
    if (e.code !== "Escape") return;

    this.handleClosePopup();
  };

  handlePushMessage = (message: IImageGeneratorMessage) => {
    const messages = this.state.messages;
    messages.push(message);
    this.setState({ messages }, () => {
      const lastMessage =
        this.messagesContainerRef.current?.children[this.messagesContainerRef.current.children.length - 1];

      lastMessage?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  };

  renderMessage = (message: IImageGeneratorMessage, messageIndex: number) => {
    const avatar =
      message.userType === EImageGeneratorMessageUserType.AI ? (
        <ChatboxRobotAvatarSvg className="kuika__ai-image-generator__avatar" />
      ) : (
        <ChatboxUserAvatarSvg className="kuika__ai-image-generator__avatar" />
      );

    return (
      <div className="kuika__ai-image-generator__message">
        {avatar}
        <div className="kuika__ai-image-generator__message-container">
          {message.text && <div className="kuika__ai-image-generator__message-text">{message.text}</div>}

          {message.generatedImages &&
            message.generatedImages.map((img, imageIndex) => (
              <div data-testid="aiGeneratedImageOnModal" className="kuika__ai-image-generator__generated-image-wrapper">
                <Image width={305} height={171} src={img.content} />

                {img.status === EImageStatus.Uploaded ? (
                  <CheckGreenSvg className="kuika__ai-image-generator__generated-image-action" />
                ) : img.status === EImageStatus.Uploading ? (
                  <SpinSvg className="kuika__ai-image-generator__generated-image-action spin" />
                ) : (
                  <UploadSvg
                    data-testid="downloadAiGeneratedImage"
                    className="kuika__ai-image-generator__generated-image-action btn"
                    onClick={() => this.handlePushToUploadQueue(messageIndex, imageIndex)}
                  />
                )}
              </div>
            ))}
        </div>
      </div>
    );
  };

  render() {
    const placement: IKuikaPopoverPlacement = ANTD_PLACEMENT_TO_KIT[this.props.placement ?? DEFAULT_ANTD_PLACEMENT];

    return (
      <KuikaPopover
        className="kuika__ai-image-generator__popover"
        position={placement.position}
        align={placement.align}
        visible={this.props.isActive}
        onVisibleChange={this.handlePopupVisibilityChange}
        content={
          // The header rides inside `content` rather than the kit's `title` slot on purpose:
          // the kit's slot renders its own `Popover.Close`, which only reports `visible=false`
          // back through `onVisibleChange`. This popover is fully controlled by `isActive`, so
          // that report closes nothing — only `handleClosePopup` (which calls `props.onClose`)
          // does. The hand-rolled closer below is therefore the one real close affordance, and
          // it also carries the E2E test id.
          <div className="kuika__ai-image-generator__container">
            <div className="kuika__ai-image-generator__header">
              <span className="kuika__ai-image-generator__header-title">Generate image with AI</span>
              <ClosePopupSvg
                data-testid="propertyAiGeneratedImageModalCloseButton"
                className="kuika__ai-image-generator__header-closer"
                onClick={() => this.handleClosePopup()}
              />
            </div>
            <div className="kuika__ai-image-generator__messages-container" ref={this.messagesContainerRef}>
              {this.state.messages.map((message, i) => this.renderMessage(message, i))}
            </div>
            <div className="kuika__ai-image-generator__form-container">
              {this.state.messages.some((msg) => msg.userType === EImageGeneratorMessageUserType.User) &&
                !this.state.isAIGeneratingImages && (
                  <a
                    data-testid="regenerateButton"
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleSendImageGenerateRequest();
                    }}
                    className="kuika__ai-image-generator__regenerate-link"
                  >
                    RE-GENERATE
                  </a>
                )}
              <Form ref={this.formRef} onFinish={this.handleSendImageGenerateRequest}>
                <Form.Item
                  name="userMessage"
                  rules={[{ required: true, message: "Please type message to generate images." }]}
                  noStyle
                >
                  <KuikaInput
                    data-testid="componentValuePropertyGenerateWithAiInput"
                    onKeyDown={this.handleInputKeydown}
                    placeholder="Type here..."
                    autoFocus
                    rightIcon={
                      this.state.isAIGeneratingImages ? (
                        <>
                          <SpinSvg className="kuika__ai-image-generator__input-suffix spin" />
                        </>
                      ) : (
                        <>
                          <ChatboxEnterSvg
                            className="kuika__ai-image-generator__input-suffix"
                            onClick={() => {
                              this.formRef.current?.submit();
                            }}
                          />
                        </>
                      )
                    }
                  />
                </Form.Item>
              </Form>
            </div>
          </div>
        }
      >
        {this.props.children}
      </KuikaPopover>
    );
  }
}
